import React, { useCallback, useEffect, useState } from 'react';
import { deriveProtocols } from './protocol';
import './SubstreamsPanel.css';

const REGISTRY = 'https://substreams.dev/v1/registry/packages';

function PackageDetail({ pkg, searchedAddress }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  const inspect = useCallback(async () => {
    if (data || state === 'loading') return;
    setState('loading');
    setError(null);
    try {
      const url = `/api/substreams-package?slug=${encodeURIComponent(pkg.slug)}&version=${encodeURIComponent(pkg.latestVersion)}`;
      const res = await fetch(url);
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setData(body);
      setState('done');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  }, [data, state, pkg.slug, pkg.latestVersion]);

  const match =
    data && searchedAddress && data.contracts.includes(searchedAddress.toLowerCase());

  return (
    <div className="ss-detail">
      {state === 'idle' && (
        <button className="ss-inspect" onClick={inspect}>
          Inspect package
        </button>
      )}
      {state === 'loading' && <div className="ss-muted">Decoding package…</div>}
      {state === 'error' && <div className="ss-error">Could not decode: {error}</div>}

      {data && (
        <>
          {match && <div className="ss-match">✓ This package indexes the contract you searched</div>}

          <div className="ss-contracts">
            {data.contractsExposed ? (
              <>
                <span className="ss-label">Contracts indexed</span>
                <ul>
                  {data.contracts.map((c) => (
                    <li key={c} className={searchedAddress && c === searchedAddress.toLowerCase() ? 'hit' : ''}>
                      <code>{c}</code>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              // Absence of params is not absence of contracts — most packages
              // hardcode addresses in Rust, where nothing outside the WASM can
              // see them. Never render this as "indexes nothing".
              <div className="ss-muted">
                Contracts are compiled into this package rather than passed as parameters, so they
                can't be read from the outside. Check the{' '}
                <a href={pkg.repository} target="_blank" rel="noopener noreferrer">
                  source
                </a>
                .
              </div>
            )}
          </div>

          <div className="ss-modules">
            <span className="ss-label">
              {data.moduleCount} module{data.moduleCount === 1 ? '' : 's'}
            </span>
            <table>
              <tbody>
                {data.modules.map((m) => (
                  <tr key={m.name}>
                    <td className={`ss-kind ss-kind-${m.kind}`}>{m.kind}</td>
                    <td className="ss-mod-name">{m.name}</td>
                    <td className="ss-block">
                      {m.initialBlock ? `from block ${m.initialBlock.toLocaleString()}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PackageCard({ pkg, searchedAddress }) {
  return (
    <div className="ss-card">
      <div className="ss-card-head">
        <h3 title={pkg.name}>{pkg.name}</h3>
        {pkg.network && <span className="ss-network">{pkg.network}</span>}
      </div>
      <p className="ss-meta">
        {pkg.downloads != null && <>{pkg.downloads.toLocaleString()} downloads · </>}
        {pkg.latestVersion}
      </p>
      <PackageDetail pkg={pkg} searchedAddress={searchedAddress} />
      <div className="ss-links">
        <a href={`https://substreams.dev/packages/${pkg.slug}`} target="_blank" rel="noopener noreferrer">
          Registry
        </a>
        {pkg.repository && (
          <a href={pkg.repository} target="_blank" rel="noopener noreferrer">
            Source
          </a>
        )}
      </div>
      <code className="ss-run">substreams gui {pkg.reference}</code>
    </div>
  );
}

export default function SubstreamsPanel({ subgraphs, searchedAddress }) {
  const [term, setTerm] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState('');

  const suggestions = deriveProtocols(subgraphs);

  const run = useCallback(async (q) => {
    if (!q) return;
    setLoading(true);
    setError(null);
    setSearched(q);
    try {
      const res = await fetch(`${REGISTRY}?query=${encodeURIComponent(q)}&pageSize=12`);
      if (!res.ok) throw new Error(`registry returned ${res.status}`);
      const body = await res.json();
      setPackages(body.packages || []);
    } catch (err) {
      setError(err.message);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run against the strongest protocol term as soon as subgraph results land.
  useEffect(() => {
    const top = deriveProtocols(subgraphs)[0];
    if (top) {
      setTerm(top);
      run(top);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subgraphs]);

  return (
    <div className="ss-panel">
      <h2>Substreams for this protocol</h2>
      <p className="ss-explain">
        Substreams packages can't be searched by contract address — a package's addresses live
        inside its compiled <code>.spkg</code>, not in the registry index. So we search by protocol
        name derived from the subgraphs above, then decode each package to show what it actually
        indexes.
      </p>

      <form
        className="ss-form"
        onSubmit={(e) => {
          e.preventDefault();
          run(term.trim());
        }}
      >
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Protocol name (e.g. uniswap)"
          className="ss-input"
        />
        <button type="submit" disabled={loading || !term.trim()}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {suggestions.length > 0 && (
        <div className="ss-suggestions">
          {suggestions.map((s) => (
            <button
              key={s}
              className={s === searched ? 'active' : ''}
              onClick={() => {
                setTerm(s);
                run(s);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {error && <div className="ss-error">Registry search failed: {error}</div>}

      {!loading && searched && packages.length === 0 && !error && (
        <div className="ss-muted">
          No substreams packages match “{searched}”. Not every protocol has one — the registry has
          far fewer packages than the subgraph network has subgraphs.
        </div>
      )}

      {packages.length > 0 && (
        <>
          <h3 className="ss-count">
            {packages.length} package{packages.length === 1 ? '' : 's'} for “{searched}”
          </h3>
          <div className="ss-grid">
            {packages.map((p) => (
              <PackageCard key={p.slug} pkg={p} searchedAddress={searchedAddress} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
